import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import classNames from 'classnames'
import styled from 'styled-typed'

import { State } from 'store'
import { User } from 'auth/state'
import { withFeatures } from 'features'
import { Container, ContainerHeading, ContainerBody, HeadingSectionName } from 'uiComponents/settingsContainer'
import { PageTitle } from 'uiComponents/typography'
import { isConviousAdmin, isPartnerAdmin } from 'utils/useGetPermission'
import { useHasAnyIntegrations } from './integrations/utils'
import Feature from 'features/feature'
import { ContractEditWarning } from './contract/contractEditWarning'
import Button from 'uiComponents/buttons'

interface AccountSettingsProps extends ReturnType<typeof mapStateToProps>, RouteComponentProps {
    hasFeature: (feature: string, accountslug: string) => boolean
    children?: React.ReactNode
}

const Title = styled(PageTitle)`
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0 !important;
`

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
`


const AccountSettingsPageContainer: React.FC<AccountSettingsProps> = ({
    accountSlug,
    accounts,
    location,
    history,
    isConviousAdmin,
    isPartnerAdmin,
    children,
}) => {
    const activeAccount = accounts.find((x) => x.slug === accountSlug) || accounts[0]
    const hasAnyIntegrations = useHasAnyIntegrations()
    const isReportingCategories = location.pathname.includes('reporting_categories')

    const handleAddNew = () => {
        const params = new URLSearchParams(location.search)
        params.set('addNew', 'true')
        history.replace(`${location.pathname}?${params.toString()}`)
    }

    return (
        <div style={{ position: 'relative' }} id="account-settings">
            <TitleRow>
                <Title title={activeAccount.name}>Account Settings - {activeAccount.name}</Title>
                {isReportingCategories && (
                    <Button kind="action" onClick={handleAddNew}>
                        Create +
                    </Button>
                )}
            </TitleRow>
            <ContractEditWarning />
            <Container>
                <ContainerHeading>
                    <HeadingSectionName
                        className={classNames({ active: location.pathname.includes('account_info') })}
                        to={`/account/${accountSlug}/settings/account_settings/account_info`}
                    >
                        Account
                    </HeadingSectionName>
                    <HeadingSectionName
                        id="payment-methods-link"
                        className={classNames({
                            active: location.pathname.includes('payment_methods'),
                        })}
                        to={`/account/${accountSlug}/settings/account_settings/payment_methods`}
                    >
                        Payment Methods
                    </HeadingSectionName>
                    <Feature name="BookingFees" accountSlug={accountSlug}>
                        <HeadingSectionName
                            id="booking-fees-link"
                            className={classNames({
                                active: location.pathname.includes('fees'),
                            })}
                            to={`/account/${accountSlug}/settings/account_settings/booking_fees`}
                        >
                            Fees
                        </HeadingSectionName>
                    </Feature>
                    <Feature name="TaxConfigurations" accountSlug={accountSlug}>
                        <HeadingSectionName
                            id="tax-configurations-link"
                            className={classNames({
                                active: location.pathname.includes('tax_configurations'),
                            })}
                            to={`/account/${accountSlug}/settings/account_settings/tax_configurations`}
                        >
                            Tax Configurations
                        </HeadingSectionName>
                    </Feature>
                    <Feature name="ReportingCategories" accountSlug={accountSlug}>
                        <HeadingSectionName
                            id="reporting-categories-link"
                            className={classNames({
                                active: location.pathname.includes('reporting_categories'),
                            })}
                            to={`/account/${accountSlug}/settings/account_settings/reporting_categories`}
                        >
                            Reporting Categories
                        </HeadingSectionName>
                    </Feature>
                    {isConviousAdmin && (
                        <>
                            <HeadingSectionName
                                id="contract-link"
                                className={classNames({
                                    active: location.pathname.includes('contract'),
                                })}
                                to={`/account/${accountSlug}/settings/account_settings/contract`}
                            >
                                Contract
                            </HeadingSectionName>
                        </>
                    )}
                    {((isPartnerAdmin && hasAnyIntegrations) || isConviousAdmin) && (
                        <HeadingSectionName
                            id="integrations-link"
                            className={classNames({
                                active: location.pathname.includes('integrations'),
                            })}
                            to={`/account/${accountSlug}/settings/account_settings/integrations`}
                        >
                            Integrations
                        </HeadingSectionName>
                    )}
                </ContainerHeading>
                <ContainerBody noPadding={location.pathname.includes('reporting_categories')}>{children}</ContainerBody>
            </Container>
        </div>
    )
}

const mapStateToProps = (state: State) => {
    return {
        accountSlug: state.preferences.activeAccount as string,
        accounts: state.auth.user ? state.auth.user.accounts : [],
        isConviousAdmin: isConviousAdmin(state.auth.user as User, state.preferences.activeAccount),
        isPartnerAdmin: isPartnerAdmin(state.auth.user as User, state.preferences.activeAccount),
    }
}

export default withRouter(withFeatures(connect(mapStateToProps)(AccountSettingsPageContainer)))
